import React, { useState, useMemo } from 'react';
import { GUITAR_CHORDS, GUITAR_STRINGS, ChordData, ChordCategory, getChordFrequencies } from '../../utils/guitarTheory';
import { soundEngine } from '../../utils/audio';
import { Sparkles, Music, Volume2, ArrowLeft, Search, Filter } from 'lucide-react';
import confetti from 'canvas-confetti';

interface AllChordsEncyclopediaProps {
  onBackToProgressions: () => void;
  initialChordId?: string;
}

const CATEGORIES: { id: ChordCategory | 'All'; label: string; emoji: string }[] = [
  { id: 'All', label: 'All Chords', emoji: '🌈' },
  { id: 'Major', label: 'Major', emoji: '☀️' },
  { id: 'Minor', label: 'Minor', emoji: '🌙' },
  { id: '7th', label: '7th & Blues', emoji: '🎷' },
  { id: 'Maj7 & Min7', label: 'Maj7 & Min7', emoji: '🌸' },
  { id: 'Suspended', label: 'Suspended & Add9', emoji: '✨' },
  { id: 'Diminished', label: 'Diminished', emoji: '🔮' },
];

const ROOTS = ['All', 'C', 'D', 'E', 'F', 'G', 'A', 'B'] as const;

export const AllChordsEncyclopedia: React.FC<AllChordsEncyclopediaProps> = ({
  onBackToProgressions,
  initialChordId,
}) => {
  const [selectedCategory, setSelectedCategory] = useState<ChordCategory | 'All'>('All');
  const [selectedRoot, setSelectedRoot] = useState<string>('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [vibratingString, setVibratingString] = useState<number | null>(null);
  const [isStrumming, setIsStrumming] = useState(false);

  // Selected chord state
  const initialChord = GUITAR_CHORDS.find((c) => c.id === initialChordId) || GUITAR_CHORDS[0];
  const [selectedChord, setSelectedChord] = useState<ChordData>(initialChord);

  // Filtered chord list
  const filteredChords = useMemo(() => {
    return GUITAR_CHORDS.filter((chord) => {
      const matchesCategory = selectedCategory === 'All' || chord.category === selectedCategory;
      const matchesRoot = selectedRoot === 'All' || chord.root === selectedRoot;
      const matchesSearch =
        searchQuery.trim() === '' ||
        chord.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        chord.symbol.toLowerCase().includes(searchQuery.toLowerCase()) ||
        chord.category.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesRoot && matchesSearch;
    });
  }, [selectedCategory, selectedRoot, searchQuery]);

  // Pluck a single string
  const handlePluckString = (stringIndex: number) => {
    const fret = selectedChord.frets[stringIndex];
    if (fret === -1) {
      soundEngine.playIncorrectSfx();
      return;
    }
    const str = GUITAR_STRINGS[stringIndex];
    const freq = str.baseFreq * Math.pow(2, fret / 12);

    setVibratingString(stringIndex);
    soundEngine.playGuitarPluck(freq, 2.4, 0.55);
    setTimeout(() => setVibratingString(null), 500);
  };

  // Strum the full chord
  const handleStrum = () => {
    setIsStrumming(true);
    soundEngine.playPopSfx();
    const freqs = getChordFrequencies(selectedChord);
    soundEngine.strumChord(freqs, 40);

    confetti({
      particleCount: 20,
      spread: 50,
      origin: { y: 0.7 },
      colors: ['#F472B6', '#C084FC', '#38BDF8'],
    });

    setTimeout(() => setIsStrumming(false), 600);
  };

  return (
    <div className="space-y-8 animate-fadeIn">
      
      {/* Top Navigation & Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-5 sm:p-6 rounded-3xl border-2 border-pink-200 shadow-sm">
        <button
          onClick={() => {
            soundEngine.playPopSfx();
            onBackToProgressions();
          }}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl bg-pink-50 hover:bg-pink-100 text-pink-700 font-bold text-xs sm:text-sm border border-pink-200 transition-all cursor-pointer self-start sm:self-auto hover:scale-102 active:scale-98"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>← Back to 4-Chord Progression Garden</span>
        </button>

        <div className="text-center sm:text-right">
          <span className="text-xs text-purple-600 font-bold bg-purple-50 px-3 py-1 rounded-full border border-purple-200">
            {filteredChords.length} of {GUITAR_CHORDS.length} Chords Showing
          </span>
        </div>
      </div>

      {/* Filter and Search Controls */}
      <div className="bg-white p-6 rounded-3xl border-2 border-pink-100 shadow-xs space-y-5">
        
        {/* Category Pills */}
        <div>
          <label className="text-xs font-bold text-purple-900 uppercase tracking-wider block mb-2.5 flex items-center gap-1.5">
            <Filter className="w-3.5 h-3.5 text-pink-500" />
            <span>Classified By Chord Type:</span>
          </label>
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((cat) => {
              const isActive = selectedCategory === cat.id;
              return (
                <button
                  key={cat.id}
                  onClick={() => {
                    soundEngine.playPopSfx();
                    setSelectedCategory(cat.id);
                  }}
                  className={`px-3.5 py-1.5 rounded-full font-bold text-xs sm:text-sm transition-all cursor-pointer flex items-center gap-1.5 ${
                    isActive
                      ? 'bg-gradient-to-r from-pink-500 to-rose-500 text-white shadow-md shadow-pink-200 scale-105'
                      : 'bg-pink-50 text-purple-800 hover:bg-pink-100'
                  }`}
                >
                  <span>{cat.emoji}</span>
                  <span>{cat.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Root Note & Search Bar Row */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pt-3 border-t border-pink-50">
          
          {/* Root Notes */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-xs font-bold text-gray-500 mr-1">Root:</span>
            {ROOTS.map((root) => {
              const isActive = selectedRoot === root;
              return (
                <button
                  key={root}
                  onClick={() => {
                    soundEngine.playPopSfx();
                    setSelectedRoot(root);
                  }}
                  className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full font-bold text-xs transition-all cursor-pointer flex items-center justify-center ${
                    isActive
                      ? 'bg-purple-600 text-white shadow-xs'
                      : 'bg-purple-50 text-purple-800 hover:bg-purple-100'
                  }`}
                >
                  {root}
                </button>
              );
            })}
          </div>

          {/* Search Box */}
          <div className="relative min-w-[220px]">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search chords (e.g. C, Dm, 7th)..."
              className="w-full pl-9 pr-4 py-2 rounded-xl border border-pink-200 text-xs focus:outline-none focus:ring-2 focus:ring-pink-400 bg-pink-50/20"
            />
          </div>

        </div>

      </div>

      {/* Main Interactive Stage for Selected Chord */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        
        {/* Left 7 cols: Interactive Chord Diagram */}
        <div className="lg:col-span-7 bg-white rounded-3xl p-6 sm:p-8 border-2 border-pink-200 shadow-md">
          
          <div className="flex items-center justify-between mb-4">
            <div>
              <span className="px-3 py-1 rounded-full bg-pink-100 text-pink-700 font-bold text-xs uppercase tracking-wide">
                {selectedChord.category} Chord
              </span>
              <h3 className="text-3xl font-extrabold text-gray-800 mt-1">
                {selectedChord.name} ({selectedChord.symbol})
              </h3>
            </div>
            
            <button
              onClick={handleStrum}
              disabled={isStrumming}
              className={`px-6 py-3 rounded-2xl bg-gradient-to-r from-pink-500 to-rose-500 text-white font-bold text-sm shadow-md hover:shadow-lg hover:scale-105 active:scale-95 transition-all flex items-center gap-2 cursor-pointer ${
                isStrumming ? 'animate-bounce' : ''
              }`}
            >
              <Music className="w-4 h-4" />
              <span>Strum Chord! 🎶</span>
            </button>
          </div>

          <p className="text-xs text-gray-500 mb-6 flex items-center gap-1.5">
            <Volume2 className="w-3.5 h-3.5 text-pink-500" />
            <span>Click any individual string to pluck it, or hit <b>Strum Chord</b>!</span>
          </p>

          {/* Graphical Chord Box */}
          <div className="max-w-sm mx-auto bg-amber-50/70 border-4 border-amber-800/60 rounded-2xl p-5 shadow-inner">
            
            {/* Top Nut / Mute-Open String Indicators */}
            <div className="grid grid-cols-6 text-center mb-2">
              {[5, 4, 3, 2, 1, 0].map((strIdx) => {
                const fret = selectedChord.frets[strIdx];
                const isMute = fret === -1;
                const isOpen = fret === 0;
                return (
                  <div key={strIdx} className="font-extrabold text-sm sm:text-base">
                    {isMute ? (
                      <span className="text-red-500">✕</span>
                    ) : isOpen ? (
                      <span className="text-emerald-500">○</span>
                    ) : (
                      <span className="text-purple-400 text-xs">fret {fret}</span>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Nut Line */}
            <div className="h-2 bg-amber-900 rounded-sm mb-1" />

            {/* Frets Grid (Frets 1 to 4) */}
            <div className="relative border-x-2 border-b-2 border-amber-900/60 bg-amber-100/40 divide-y-2 divide-amber-900/50">
              {[1, 2, 3, 4].map((fretNum) => (
                <div key={fretNum} className="relative h-14 sm:h-16 flex items-center">
                  <span className="absolute -left-7 text-[11px] font-bold text-amber-900/70">
                    F{fretNum}
                  </span>

                  <div className="w-full grid grid-cols-6 h-full relative">
                    {[5, 4, 3, 2, 1, 0].map((strIdx) => {
                      const chordFret = selectedChord.frets[strIdx];
                      const finger = selectedChord.fingers[strIdx];
                      const hasFingerOnThisFret = chordFret === fretNum;
                      const isVibrating = vibratingString === strIdx;

                      return (
                        <div
                          key={strIdx}
                          onClick={() => handlePluckString(strIdx)}
                          className="relative h-full flex items-center justify-center cursor-pointer group"
                        >
                          <div
                            className={`absolute top-0 bottom-0 transition-transform ${
                              isVibrating ? 'string-vibrating bg-pink-500' : 'bg-amber-800/80 group-hover:bg-pink-400'
                            }`}
                            style={{
                              width: `${Math.max(1.5, 4 - strIdx * 0.5)}px`,
                            }}
                          />

                          {hasFingerOnThisFret && (
                            <div className="relative z-10 w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gradient-to-tr from-pink-500 to-rose-400 text-white font-extrabold text-xs sm:text-sm flex items-center justify-center shadow-md animate-scaleIn border-2 border-white">
                              {finger > 0 ? finger : '●'}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* String Notes Label Row */}
            <div className="grid grid-cols-6 text-center mt-3">
              {[5, 4, 3, 2, 1, 0].map((strIdx) => {
                const str = GUITAR_STRINGS[strIdx];
                return (
                  <button
                    key={strIdx}
                    onClick={() => handlePluckString(strIdx)}
                    className="p-1 rounded-lg hover:bg-pink-100 transition-colors cursor-pointer"
                    title={`Click to pluck ${str.name}`}
                  >
                    <div className="text-xs font-bold text-gray-700">{str.baseNote}</div>
                    <div className="text-[10px] text-gray-400">{strIdx === 0 ? 'High' : strIdx === 5 ? 'Low' : ''}</div>
                  </button>
                );
              })}
            </div>

          </div>

          <div className="mt-5 flex flex-wrap items-center justify-center gap-3 text-xs text-gray-600 bg-pink-50/50 p-2.5 rounded-xl border border-pink-100">
            <span className="font-bold text-purple-900">Finger Numbers:</span>
            <span>① Index</span>
            <span>② Middle</span>
            <span>③ Ring</span>
            <span>④ Pinky</span>
          </div>

        </div>

        {/* Right 5 cols: Chord Details & Chord Grid Browser */}
        <div className="lg:col-span-5 space-y-4">
          
          {/* Selected Chord Card */}
          <div className="rounded-3xl p-6 bg-gradient-to-br from-pink-100 via-purple-100 to-rose-100 border-2 border-pink-200 shadow-sm space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wider text-pink-700 flex items-center gap-1">
                <Sparkles className="w-3.5 h-3.5" />
                <span>{selectedChord.category}</span>
              </span>
              <span className="text-xs font-semibold text-purple-700 bg-white/70 px-2.5 py-0.5 rounded-full">
                {selectedChord.difficulty}
              </span>
            </div>

            <div className="text-xl font-bold text-purple-950">
              {selectedChord.kawaiiVibe}
            </div>
            <p className="text-sm text-purple-900/80 leading-relaxed">
              {selectedChord.description}
            </p>
          </div>

          {/* Quick Chord Selection Grid */}
          <div className="bg-white rounded-3xl p-5 border-2 border-pink-100 shadow-xs space-y-3">
            <div className="flex items-center justify-between text-xs font-bold text-gray-700">
              <span>Choose Chord to Inspect:</span>
              <span className="text-pink-600">{filteredChords.length} available</span>
            </div>

            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-[340px] overflow-y-auto pr-1">
              {filteredChords.map((chord) => {
                const isSelected = selectedChord.id === chord.id;
                return (
                  <button
                    key={chord.id}
                    onClick={() => {
                      soundEngine.playPopSfx();
                      setSelectedChord(chord);
                    }}
                    className={`p-2.5 rounded-xl border-2 text-center transition-all cursor-pointer ${
                      isSelected
                        ? 'border-pink-500 bg-pink-50 ring-2 ring-pink-200 font-extrabold text-pink-700 shadow-xs scale-102'
                        : 'border-pink-100 hover:border-pink-300 hover:bg-pink-50/30 text-gray-700'
                    }`}
                  >
                    <div className="text-base font-extrabold">{chord.symbol}</div>
                    <div className="text-[10px] text-gray-500 truncate">{chord.name}</div>
                  </button>
                );
              })}
            </div>
          </div>

        </div>

      </div>

    </div>
  );
};
